/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { Field as ReduxField } from 'redux-form';
import { Alert, Modal } from 'react-bootstrap';
import { STOREDGE } from '../../../../constants/softwareProviders';
import { createValidator, required } from '../../../../src/web/utils/validation';
import Form from '../../../../modules/react/components/stateful/Form';
import Input from '../../../../modules/react/components/stateful/Input';
import Field from '../../../../modules/react/components/stateful/Field';
import Button from '../../../../modules/react/components/stateless/Button';
import CheckboxInput from '../../../../modules/react/components/stateful/Checkbox/Checkbox';
import StarRatingInput from '../../../../modules/react/components/stateful/StarRating/StarRating';
import Translation from '../../stateless/Translation';
import LiveFacilitySelectInput from '../../../../src/web/containers/form/inputs/LiveFacilitySelectInput';
import withAlert from '../../../hocs/withAlert';
import styles from './ReviewModal.scss';
import TextareaInput from '../../../../modules/react/components/stateful/TextareaInput/TextareaInput';

const propTypes = {
  alert: PropTypes.node,
  facilityId: PropTypes.string.isRequired,
  show: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  error: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
  })),
  emailValue: PropTypes.string,
};

const defaultProps = {
  alert: undefined,
  show: false,
  isSubmitting: false,
  error: [],
  emailValue: undefined,
};

const FORM_NAME = 'review-modal';

function renderError(error) {
  if (!error) {
    return null;
  }

  return (
    <Alert variant="danger">
      {error.map(({ message }, index) => <p key={index}>{message}</p>)}
    </Alert>
  );
}

const validate = createValidator({
  rating: required,
  facilityId: required,
});

const ReviewModal = ({
  alert,
  facilityId,
  show = false,
  isSubmitting = false,
  onSubmit,
  onCancel,
  error,
  emailValue,
}) => (
  <Modal onHide={onCancel} keyboard show={show} className={styles.modalTitle}>
    <Form
      form={FORM_NAME}
      onSubmit={onSubmit}
      initialValues={{ facilityId }}
      validate={validate}
      onlyValidateOnSubmit
      destroyOnUnmount={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="color-primary-foreground">
          <Translation id="widgets.reviewButton.reviewModal.title" defaultMessage="Write a Review" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {alert && alert}
        </div>
        {renderError(error)}
        <Translation id="widgets.reviewButton.reviewModal.form.rating" defaultMessage="Overall Rating">
          {text => (
            <Field
              label={text}
              id="reviewModal-rating"
              name="rating"
              component={StarRatingInput}
              required
            />
          )}
        </Translation>
        <Translation id="widgets.reviewButton.reviewModal.form.facilityId" defaultMessage="Facility">
          {text => (
            <Field
              label={text}
              id="reviewModal-facilityId"
              name="facilityId"
              emptyOption="Select Facility"
              component={LiveFacilitySelectInput}
              required
            />
          )}
        </Translation>
        <Translation
          id="widgets.reviewButton.reviewModal.form.body"
          defaultMessage="Describe Your Experience"
        >
          {text => (
            <Field
              label={text}
              id="reviewModal-body"
              name="body"
              component={TextareaInput}
              componentClass="textarea"
            />
          )}
        </Translation>
        <Translation id="widgets.reviewButton.reviewModal.form.name" defaultMessage="Name">
          {text => (
            <Field
              label={text}
              id="reviewModal-name"
              name="name"
              component={Input}
            />
          )}
        </Translation>
        <Translation id="widgets.reviewButton.reviewModal.form.email" defaultMessage="Email">
          {text => (
            <Field
              label={text}
              id="reviewModal-email"
              name="email"
              type="email"
              component={Input}
            />
          )}
        </Translation>
        {emailValue && emailValue.trim() ? (
          <Translation
            id="widgets.reviewButton.reviewModal.form.requestFollowup"
            defaultMessage="Would you like to be contacted about this review?"
          >
            {text => (
              <ReduxField
                label={text}
                name="requestFollowup"
                component={CheckboxInput}
              />
            )}
          </Translation>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Translation id="widgets.reviewButton.reviewModal.cancel" defaultMessage="Cancel">
          {text => <Button onClick={onCancel} variant="link">{text}</Button>}
        </Translation>
        <Button
          type="submit"
          variant="primary"
          className="color-secondary-background color-primary-background-hover button-style-class"
          label="submit"
          disabled={isSubmitting}
        >
          <Translation
            {...(isSubmitting
              ? {
                id: 'widgets.reviewButton.reviewModal.form.submitting',
                defaultMessage: 'Submitting...',
              } : {
                id: 'widgets.reviewButton.reviewModal.form.submit',
                defaultMessage: 'Submit',
              })
            }
          />
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>
);

ReviewModal.propTypes = propTypes;
ReviewModal.defaultProps = defaultProps;

// We only care if storEDGE is alerting because that's where we store reviews.
export default connect(
  ({ form }) => ({
    emailValue: get(form, [FORM_NAME, 'values', 'email']),
  }),
  {},
)(withAlert({
  Component: ReviewModal,
  message: 'We sincerely appreciate you taking the time to leave a review, and we do value your feedback. Please try again later!',
  providers: [STOREDGE],
}));
